import { z } from "zod/v4";
import {
  COPY_EXAMPLES_CHANNELS,
  COPY_EXAMPLES_MAX_BODY_LENGTH,
  COPY_EXAMPLES_MAX_RAW_BODY_LENGTH,
  COPY_EXAMPLES_MAX_EXAMPLES,
  COPY_EXAMPLES_MAX_IMAGE_URLS,
  COPY_EXAMPLES_MAX_SHORT_TEXT_LENGTH,
  COPY_EXAMPLES_ROW_FILTER_NOTE_MAX_LENGTH,
  COPY_EXAMPLES_SCRATCH_FILENAME,
  type CopyExample,
  type CopyExamplesContent,
  type CopyExamplesExtractionParams,
  type CopyExamplesResolvedSource,
  type CopyExamplesReviewJudgment,
  type CopyExamplesReviewOutputContent,
  type CopyExamplesScratchFile,
  type CopyExamplesScratchRecord,
  type CopyExamplesSource,
  type CopyExampleSourceModality,
  type CopyExamplesTarget,
} from "./types";

// Stamped onto server-constructed content and used as the parse default so pre-versioning rows
// read back as v1. Bump when the stored shape changes incompatibly.
export const COPY_EXAMPLES_SCHEMA_VERSION = 1;

const nullableBoundedStringInputSchema = z
  .string()
  .max(COPY_EXAMPLES_MAX_SHORT_TEXT_LENGTH)
  .nullable()
  .optional();

const copyExamplesResolvedSourceInputSchema = z.object({
  table: z.string().min(1).max(COPY_EXAMPLES_MAX_SHORT_TEXT_LENGTH),
  columns: z.object({
    id: z.string().min(1).max(COPY_EXAMPLES_MAX_SHORT_TEXT_LENGTH),
    name: nullableBoundedStringInputSchema,
    subject: nullableBoundedStringInputSchema,
    preheader: nullableBoundedStringInputSchema,
    body: z.string().min(1).max(COPY_EXAMPLES_MAX_SHORT_TEXT_LENGTH),
    updated_at: z.string().min(1).max(COPY_EXAMPLES_MAX_SHORT_TEXT_LENGTH),
    is_draft: nullableBoundedStringInputSchema,
    is_archived: nullableBoundedStringInputSchema,
  }),
});

// `row_filter_note` is optional-and-nullable on the wire so legacy stored params and agent
// omissions parse cleanly. Bounded (flows into the ingestion prompt) and non-executable. Normalized
// (undefined -> null) in normalizeExtractionParams so the domain type keeps a required `string | null`.
export const copyExamplesExtractionParamsSchema = z.object({
  lookback_days: z.number().int().positive(),
  min_sends: z.number().int().nonnegative().nullable(),
  target_count: z.number().int().positive(),
  row_filter_note: z
    .string()
    .max(COPY_EXAMPLES_ROW_FILTER_NOTE_MAX_LENGTH)
    .nullable()
    .optional(),
});

const copyExampleSourceModalitySchema = z.enum(["text", "image_ocr"]);

// image_urls entries render as <img src> in the review UI, so only https URLs are storable:
// other schemes are useless there at best, and both writer paths (the extraction agent's rows and
// the entity-update input) accept caller-authored strings.
function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

const copyExampleImageUrlInputSchema = z
  .string()
  .min(1)
  .max(COPY_EXAMPLES_MAX_SHORT_TEXT_LENGTH)
  .refine(isHttpsUrl, "image_urls entries must be https URLs");

const copyExampleInputSchema = z.object({
  id: z.string().min(1).max(COPY_EXAMPLES_MAX_SHORT_TEXT_LENGTH),
  source_id: nullableBoundedStringInputSchema,
  subject: z.string().max(COPY_EXAMPLES_MAX_SHORT_TEXT_LENGTH).optional(),
  preheader: z.string().max(COPY_EXAMPLES_MAX_SHORT_TEXT_LENGTH).optional(),
  title: z.string().max(COPY_EXAMPLES_MAX_SHORT_TEXT_LENGTH).optional(),
  cta: z.string().max(COPY_EXAMPLES_MAX_SHORT_TEXT_LENGTH).optional(),
  // May be empty: a reviewer can clear the body and keep only a title or CTA (e.g. an ad headline).
  body: z.string().max(COPY_EXAMPLES_MAX_BODY_LENGTH),
  raw_body: z.string().max(COPY_EXAMPLES_MAX_RAW_BODY_LENGTH).optional(),
  campaign_name: nullableBoundedStringInputSchema,
  sent_at: nullableBoundedStringInputSchema,
  excluded: z.boolean(),
  exclusion_reason: nullableBoundedStringInputSchema,
  // Optional; absent on legacy rows and text-only extractions examples.
  source_modality: copyExampleSourceModalitySchema.optional(),
  image_urls: z
    .array(copyExampleImageUrlInputSchema)
    .max(COPY_EXAMPLES_MAX_IMAGE_URLS)
    .optional(),
});

const copyExamplesTargetInputSchema = z.object({
  destination_id: nullableBoundedStringInputSchema,
});

// Discriminated on `kind`. z.object strips unknown keys, so legacy manual objects still carrying
// `fivetran_sync_id`/`resolved_source: null` parse cleanly into the manual variant.
const copyExamplesSourceInputSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("fivetran"),
    fivetran_sync_id: z
      .string()
      .min(1)
      .max(COPY_EXAMPLES_MAX_SHORT_TEXT_LENGTH),
    resolved_source: copyExamplesResolvedSourceInputSchema,
  }),
  z.object({
    kind: z.literal("warehouse"),
    connection_id: z.string().min(1).max(COPY_EXAMPLES_MAX_SHORT_TEXT_LENGTH),
    resolved_source: copyExamplesResolvedSourceInputSchema,
  }),
  z.object({
    kind: z.literal("manual"),
  }),
  z.object({
    kind: z.literal("meta_ad_library"),
    page_id: z.string().min(1).max(COPY_EXAMPLES_MAX_SHORT_TEXT_LENGTH),
    page_name: nullableBoundedStringInputSchema,
    country: z.string().min(2).max(8),
  }),
]);

// Plain-object (non-transforming) shape of copy_examples content, exported so model-facing agent
// schemas can be converted to JSON Schema. Server-side parsing goes through parseCopyExamplesContent.
export const copyExamplesContentInputSchema = z.object({
  channel: z.enum(COPY_EXAMPLES_CHANNELS),
  // target is server-authoritative; defaulted for entities created before the target field existed.
  target: copyExamplesTargetInputSchema.default({ destination_id: null }),
  source: copyExamplesSourceInputSchema,
  extraction_params: copyExamplesExtractionParamsSchema,
  examples: z.array(copyExampleInputSchema).max(COPY_EXAMPLES_MAX_EXAMPLES),
});

// Stored-content shape: the wire schema plus server-authoritative `schema_version` and a refinement
// rejecting duplicate example ids (reviewer overrides key by id, so duplicates would mis-apply).
// Kept separate so the wire schema stays transform/refinement-free and JSON-Schema convertible.
export const copyExamplesStoredContentInputSchema =
  copyExamplesContentInputSchema
    .extend({
      schema_version: z
        .number()
        .int()
        .positive()
        .default(COPY_EXAMPLES_SCHEMA_VERSION),
    })
    .superRefine((value, ctx) => {
      const seen = new Set<string>();
      for (const [index, example] of value.examples.entries()) {
        if (seen.has(example.id)) {
          ctx.addIssue({
            code: "custom",
            message: `duplicate example id "${example.id}"`,
            path: ["examples", index, "id"],
          });
        }
        seen.add(example.id);
      }
    });

const copyExamplesScratchRecordInputSchema = copyExampleInputSchema
  .omit({
    excluded: true,
    exclusion_reason: true,
  })
  .extend({
    // Extraction must still recover body copy; only reviewer edits may leave it empty.
    body: z.string().min(1).max(COPY_EXAMPLES_MAX_BODY_LENGTH),
    body_fingerprint: z.string().length(64),
  })
  // The image-aware extraction pass writes intermediate-only fields before
  // --resolve-email-ocr. Reject those fields here so an unresolved scratch file
  // cannot be mistaken for final copy and persisted.
  .strict();

const copyExamplesScratchFileInputSchema = z.object({
  examples: z
    .array(copyExamplesScratchRecordInputSchema)
    .min(1)
    .max(COPY_EXAMPLES_MAX_EXAMPLES),
});

const copyExamplesReviewJudgmentInputSchema = z.object({
  scratch_id: z.string().min(1).max(COPY_EXAMPLES_MAX_SHORT_TEXT_LENGTH),
  excluded: z.boolean(),
  exclusion_reason: nullableBoundedStringInputSchema,
  cleaned_body: z
    .string()
    .min(1)
    .max(COPY_EXAMPLES_MAX_BODY_LENGTH)
    .nullable()
    .optional(),
});

// Plain-object (non-transforming) shape of the copy_examples_review output, exported so the agent
// runtime can convert it to JSON Schema. Parsing goes through parseCopyExamplesReviewOutputContent.
export const copyExamplesReviewOutputContentInputSchema = z.object({
  summary: z.string().min(1).max(COPY_EXAMPLES_MAX_SHORT_TEXT_LENGTH),
  name: z.string().min(1).max(COPY_EXAMPLES_MAX_SHORT_TEXT_LENGTH),
  description: z.string().min(1).max(COPY_EXAMPLES_MAX_SHORT_TEXT_LENGTH),
  channel: z.enum(COPY_EXAMPLES_CHANNELS),
  source: copyExamplesSourceInputSchema,
  extraction_params: copyExamplesExtractionParamsSchema,
  scratch_file: z.object({
    filePath: z.literal(COPY_EXAMPLES_SCRATCH_FILENAME),
  }),
  examples: z
    .array(copyExamplesReviewJudgmentInputSchema)
    .min(1)
    .max(COPY_EXAMPLES_MAX_EXAMPLES),
});

export function parseCopyExamplesContent(value: unknown): CopyExamplesContent {
  return normalizeContent(copyExamplesStoredContentInputSchema.parse(value));
}

// Non-throwing variant for the read path: one malformed stored row must not brick context loading
// for the whole workspace. Callers log + skip the bad row on failure.
export type SafeParseCopyExamplesContentResult = {
  // Non-null on success; null when the stored content failed validation.
  content: CopyExamplesContent | null;
  // Non-null only when `content` is null.
  error: z.ZodError | null;
};

export function safeParseCopyExamplesContent(
  value: unknown,
): SafeParseCopyExamplesContentResult {
  const parsed = copyExamplesStoredContentInputSchema.safeParse(value);
  if (!parsed.success) return { content: null, error: parsed.error };
  return { content: normalizeContent(parsed.data), error: null };
}

export function parseCopyExamplesScratchFile(
  value: unknown,
): CopyExamplesScratchFile {
  const parsed = copyExamplesScratchFileInputSchema.parse(value);
  return {
    examples: parsed.examples.map(normalizeScratchRecord),
  };
}

export function parseCopyExamplesReviewOutputContent(
  value: unknown,
): CopyExamplesReviewOutputContent {
  const parsed = copyExamplesReviewOutputContentInputSchema.parse(value);
  return {
    summary: parsed.summary,
    name: parsed.name,
    description: parsed.description,
    channel: parsed.channel,
    source: normalizeSource(parsed.source),
    extraction_params: normalizeExtractionParams(parsed.extraction_params),
    scratch_file: parsed.scratch_file,
    examples: parsed.examples.map(normalizeReviewJudgment),
  };
}

function normalizeContent(
  value: z.output<typeof copyExamplesStoredContentInputSchema>,
): CopyExamplesContent {
  return {
    schema_version: value.schema_version,
    channel: value.channel,
    target: normalizeTarget(value.target),
    source: normalizeSource(value.source),
    extraction_params: normalizeExtractionParams(value.extraction_params),
    examples: value.examples.map(normalizeExample),
  };
}

// undefined (legacy rows / agent omission) collapses to null so the domain type keeps a required
// `string | null`.
function normalizeExtractionParams(
  value: z.output<typeof copyExamplesExtractionParamsSchema>,
): CopyExamplesExtractionParams {
  return {
    lookback_days: value.lookback_days,
    min_sends: value.min_sends,
    target_count: value.target_count,
    row_filter_note: value.row_filter_note ?? null,
  };
}

function normalizeTarget(
  value: z.output<typeof copyExamplesTargetInputSchema>,
): CopyExamplesTarget {
  return {
    destination_id: value.destination_id ?? null,
  };
}

// Returns the union member matching `kind`.
function normalizeSource(
  value: z.output<typeof copyExamplesSourceInputSchema>,
): CopyExamplesSource {
  switch (value.kind) {
    case "fivetran":
      return {
        kind: "fivetran",
        fivetran_sync_id: value.fivetran_sync_id,
        resolved_source: normalizeResolvedSource(value.resolved_source),
      };
    case "warehouse":
      return {
        kind: "warehouse",
        connection_id: value.connection_id,
        resolved_source: normalizeResolvedSource(value.resolved_source),
      };
    case "manual":
      return { kind: "manual" };
    case "meta_ad_library":
      return {
        kind: "meta_ad_library",
        page_id: value.page_id,
        page_name: value.page_name ?? null,
        country: value.country,
      };
  }
}

function normalizeResolvedSource(
  value: z.output<typeof copyExamplesResolvedSourceInputSchema>,
): CopyExamplesResolvedSource {
  return {
    table: value.table,
    columns: {
      id: value.columns.id,
      name: value.columns.name ?? null,
      subject: value.columns.subject ?? null,
      preheader: value.columns.preheader ?? null,
      body: value.columns.body,
      updated_at: value.columns.updated_at,
      is_draft: value.columns.is_draft ?? null,
      is_archived: value.columns.is_archived ?? null,
    },
  };
}

function normalizeExample(
  value: z.output<typeof copyExampleInputSchema>,
): CopyExample {
  const example: CopyExample = {
    id: value.id,
    source_id: value.source_id ?? null,
    body: value.body,
    campaign_name: value.campaign_name ?? null,
    sent_at: value.sent_at ?? null,
    excluded: value.excluded,
    exclusion_reason: value.exclusion_reason ?? null,
  };
  if (value.subject !== undefined) example.subject = value.subject;
  if (value.preheader !== undefined) example.preheader = value.preheader;
  if (value.title !== undefined) example.title = value.title;
  if (value.cta !== undefined) example.cta = value.cta;
  if (value.raw_body !== undefined) example.raw_body = value.raw_body;
  if (value.source_modality !== undefined) {
    example.source_modality =
      value.source_modality as CopyExampleSourceModality;
  }
  if (value.image_urls !== undefined) example.image_urls = value.image_urls;
  return example;
}

function normalizeScratchRecord(
  value: z.output<typeof copyExamplesScratchRecordInputSchema>,
): CopyExamplesScratchRecord {
  const example: CopyExamplesScratchRecord = {
    id: value.id,
    source_id: value.source_id ?? null,
    body: value.body,
    campaign_name: value.campaign_name ?? null,
    sent_at: value.sent_at ?? null,
    body_fingerprint: value.body_fingerprint,
  };
  if (value.subject !== undefined) example.subject = value.subject;
  if (value.preheader !== undefined) example.preheader = value.preheader;
  if (value.title !== undefined) example.title = value.title;
  if (value.cta !== undefined) example.cta = value.cta;
  if (value.raw_body !== undefined) example.raw_body = value.raw_body;
  if (value.source_modality !== undefined) {
    example.source_modality =
      value.source_modality as CopyExampleSourceModality;
  }
  if (value.image_urls !== undefined) example.image_urls = value.image_urls;
  return example;
}

function normalizeReviewJudgment(
  value: z.output<typeof copyExamplesReviewJudgmentInputSchema>,
): CopyExamplesReviewJudgment {
  return {
    scratch_id: value.scratch_id,
    excluded: value.excluded,
    exclusion_reason: value.exclusion_reason ?? null,
    cleaned_body: value.cleaned_body ?? null,
  };
}
